import { Content, Button, H1 } from 'components/common'
import { React, Image, styled, mq } from 'x'
import { compose, pure, lifecycle, withState } from 'recompose'

import Arrow from 'static/svg/arrow.svg'

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;
  flex-direction: column;
  justify-content: space-between;
`

const Slider = styled.div`
  width: 100%;
`

const Inner = styled.div`
  width: ${props => props.numSlides * 100}%;
  display: flex;
  min-height: 100%;
  position: relative;
  transform: translateX(-${props => props.index * (100 / props.numSlides)}%);
  transition: transform 0.7s cubic-bezier(0.645, 0.045, 0.355, 1);
`

const ContentWrapper = styled.div`
  display:flex;
  flex-direction: column;
  justify-content: flex-end;
  background-repeat: no-repeat;
  background-position: right 0 bottom -100px;
  background-size: auto 100%;
  max-width: none;
  width: ${props => 100 / props.numSlides}%;
  opacity: ${props => (props.isActive ? 1 : 0)};
  transition: opacity 0.5s ease-out;
  padding-top: 160px;
  padding-bottom:120px;

  @media (min-width: 2000px) {
    min-height:400px;
    background-size: auto 90%;
    background-position-x: calc(50% + 400px);
  }

  ${mq.below('lg')} {
    padding-top: 30px;
    padding-bottom: 120px;
    background-size: contain;
    background-position: right -60px bottom -100px;
  }

  ${mq.below('md')} {
    padding-bottom: 160px;
    background-size:60%;
    background-position: center bottom -50px;
  }

  ${mq.below('sm')} {
    background-size:contain;
    background-position: center bottom -50px;
  }

`

const StyledContent = styled(Content)`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  position: relative;
  box-sizing: border-box;
  height:100%;

  @media (min-width: 2000px) {
    max-width:1366px;
    justify-content: center;
  }

  ${mq.below('lg')} {
    padding: 0 40px;
  }

  ${mq.below('md')} {
    justify-content: flex-start;
  }
`

const DesktopBanner = styled.div`
  ${mq.below('sm')} {
      display:none !important;
  }
`

const MobileBanner = styled.div`
  ${mq.above('sm')} {
    display:none !important;
  }
  ${mq.below('sm')} {
    display:block;
    margin:0 auto;
    > div {
      max-width: 512px !important;
      margin:0 auto;
    } 
  }
`

const Title = styled(H1)`
  font-size: 42px;  
  color: #3e4d72;
  margin-bottom: 30px;
  font-weight:800;
  line-height:1.2;
  width:55%;

  ${mq.below('lg')} {
    width:45%;
    font-size:32px;
    line-height:1.2;
    margin-bottom:20px;
  }

  ${mq.below('md')} {
    width:100%;
    font-size:26px;
    line-height:1.2;
  }
`

const Subtitle = styled.p`
  font-size: 24px;
  line-height: 30px;
  margin-bottom:0;
  color: #3e4d72;
  margin: 30px 0;
  font-weight: 800;
  width:55%;

  ${mq.below('lg')} {
    width:45%;
    margin-bottom:0;
  }

  ${mq.below('md')} {
    width:100%;
    margin:0;
    font-size:20px;
    line-height:1.2;
  }
`

const AnimatedButton = styled(Button)`
  height: 46px;
  line-height: 46px;
  min-width: 200px;
  font-size:20px;
  font-weight: 800;
  margin-top:30px;
  text-transform: capitalize;
  color: #3e4d72;
  & svg {
    transition: transform 0.3s;
    &.circle {
      background-color: #3e4d72;;
      fill: #ffffff;
      opacity:0.7;
    }
  }
  &:hover {
    color: #3e4d72;
    & svg {
      transform: translateX(10px);
      &.circle {
        opacity:1;
      }
    }
  }

  ${mq.below('md')} {
    min-width:120px;
  }
`

const Lower = styled.div`
  padding-top:20px;
  width: 100%;
  z-index: 2;
  .auto-height {
    min-height:auto;
  }
`

const Dot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 15px;
  margin-right: 7px;
  cursor: pointer;
  border: 2px solid
    ${props => (props.isActive ? '#fff' : 'rgba(255, 255, 255, .5)')};
  background: ${props =>
    props.isActive ? 'rgba(255, 255, 255, .37)' : 'transparent'};

  &:hover {
    color: #fff;
    transform: scale(1.1);
  }
`

const Dots = styled.div`
  display: flex;
  margin-top: 20px;
  align-self: flex-start;
  padding-bottom: 10px;
`

const enhance = compose(
  pure,
  withState('index', 'setIndex', 0),
  withState('timeout', 'setTimeout', 0),
  lifecycle({
    componentDidMount() {
      if (this.props.data.slider && this.props.data.sldHeroAutoloop) {
        const startTimer = () =>
          this.props.setTimeout(
            setTimeout(() => {
              const numSlides = this.props.data.slider.length

              if (this.props.index + 1 > numSlides - 1) {
                this.props.setIndex(0)
              } else {
                this.props.setIndex(this.props.index + 1)
              }

              startTimer()
            }, parseInt(this.props.data.sldHeroDuration, 10))
          )

        startTimer()
      }
    },

    componentWillUnmount() {
      clearTimeout(this.props.timeout)
    }
  })
)

const HeroSlider = props => {
  const slides = props.data.slider
    ? props.data.slider
    : [
        {
          bgColour: props.data.bgColour,
          image: props.data.image,
          imageMobile: props.data.imageMobile,
          title: props.data.title,
          subtitle: props.data.subtitle,
          ctaLabel: props.data.ctaLabel,
          ctaUrl: props.data.ctaUrl
        }
      ]

  const numSlides = slides.length
  return (
    <Container>
      <Slider>
        <Inner index={props.index} numSlides={numSlides}>
          {slides.map((slide, index) => (
            <ContentWrapper 
            numSlides={numSlides} 
            key={'slide-' + index} 
            isActive={props.index === index} 
            style={{
              backgroundColor: slide.bgColour || 'white', 
              backgroundImage: slide.isImageOnlySlide != true ? `url(${slide.image.imgixImageWp.imageUrl})` : 'none'
            }} 
            >
              <StyledContent numSlides={numSlides} className={slide.isImageOnlySlide != true ? '': 'auto-height'}>
                <div>
                  {slide.isImageOnlySlide === true &&
                    <div>
                    {slide?.image?.imgixImageWp?.fluid &&
                      <DesktopBanner>
                        <Image
                        fluid={{ ...slide.image.imgixImageWp.fluid, sizes: '100vw' }}
                        style={{
                          width: '100%',
                          height: 'auto',
                          maxWidth: '100%',
                        }}
                      />
                      </DesktopBanner>
                    }

                    {slide?.imageMobile?.imgixImageWp?.fluid &&
                      <MobileBanner>
                        <Image
                          fluid={{ ...slide.imageMobile.imgixImageWp.fluid, sizes: '100vw' }}
                          style={{
                            width: '100%',
                            height: 'auto',
                            maxWidth: '100%',
                          }}
                        />
                      </MobileBanner>
                    }
                    </div>
                  }
                  {slide.title && <Title>{slide.title}</Title>}
                  {slide.subtitle && <Subtitle>{slide.subtitle}</Subtitle>}
                  {slide.ctaLabel && (
                    <AnimatedButton to={slide.ctaUrl}>
                      <span>{slide.ctaLabel}</span>
                      <Arrow className="circle" />
                    </AnimatedButton>
                  )}
                </div>
              </StyledContent>
              <Lower>
                {numSlides > 1 && (
                  <StyledContent className='auto-height'>
                    <Dots>
                      {slides.map((slide, index) => (
                        <Dot
                          key={'dot-' + index}
                          isActive={props.index === index}
                          onClick={() => props.setIndex(index)
                          }
                        />
                      ))}
                    </Dots>
                  </StyledContent>
                )}
              </Lower>
            </ContentWrapper>
          ))}
        </Inner>
      </Slider>
    </Container>
  )
}

export default enhance(HeroSlider)
